import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fmtINR } from "@/lib/api";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, updateQuantity, removeItem, totals, gst, total } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-14 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto" />
        <h1 className="font-heading text-3xl font-bold text-[#0A192F] mt-4">Your cart is empty</h1>
        <p className="text-slate-600 mt-2">Browse our GPS devices and add products to get started.</p>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF6D00] text-white font-semibold">Shop Products <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="cart-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Shopping Cart</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-4 p-4 rounded-2xl border border-slate-200 bg-white" data-testid={`cart-line-${idx}`}>
              <img src={it.image_url} alt={it.name} className="w-24 h-24 rounded-lg object-cover" />
              <div className="flex-1">
                <Link to={`/products/${it.slug}`} className="font-semibold text-[#0A192F] hover:text-[#FF6D00]">{it.name}</Link>
                <div className="text-xs text-slate-500 mt-1">
                  {it.subscription !== "none" ? `${it.subscription} plan` : "no plan"} · {it.installation ? "with installation" : "self-install"}
                  {it.vehicle_type ? ` · ${it.vehicle_type}` : ""}
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden">
                    <button onClick={() => updateQuantity(idx, it.quantity - 1)} className="w-8 h-8 hover:bg-slate-50">−</button>
                    <span className="w-8 h-8 flex items-center justify-center text-sm font-semibold">{it.quantity}</span>
                    <button onClick={() => updateQuantity(idx, it.quantity + 1)} className="w-8 h-8 hover:bg-slate-50">+</button>
                  </div>
                  <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-[#0A192F]">{fmtINR(it.price * it.quantity)}</div>
                {it.installation && <div className="text-xs text-slate-500">+ {fmtINR(it.installation_charge * it.quantity)} install</div>}
                {it.subscription !== "none" && (
                  <div className="text-xs text-slate-500">
                    + {fmtINR((it.subscription === "monthly" ? it.monthly_subscription : it.yearly_subscription) * it.quantity)} {it.subscription}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <aside className="p-6 rounded-2xl bg-slate-50 border border-slate-200 h-fit sticky top-24" data-testid="cart-summary">
          <h2 className="font-heading text-lg font-semibold text-[#0A192F]">Order summary</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtINR(totals.subtotal)}</span></div>
            {totals.installation > 0 && <div className="flex justify-between text-slate-600"><span>Installation</span><span>{fmtINR(totals.installation)}</span></div>}
            {totals.subscription > 0 && <div className="flex justify-between text-slate-600"><span>Subscription</span><span>{fmtINR(totals.subscription)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>GST (18%)</span><span>{fmtINR(gst)}</span></div>
            <div className="pt-3 border-t border-slate-200 flex justify-between font-heading font-bold text-lg text-[#0A192F]">
              <span>Total</span><span>{fmtINR(total)}</span>
            </div>
          </div>
          <Link to="/checkout" className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#FF6D00] text-white font-semibold hover:bg-[#ff8433] transition-colors" data-testid="proceed-checkout">
            Proceed to Checkout <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-3 text-xs text-slate-500 text-center">Prices inclusive of GST. Secure checkout with Razorpay.</p>
        </aside>
      </div>
    </div>
  );
}
